import { AxiosRequestConfig } from "axios";
import { IColumn } from "entities/ApiModel/IColumn";
import { LocalStorageKeys } from "utils/data/enum";
import { SelectFieldOption } from "controls/global/select-field/SelectInput";
import { SignatureDefault } from "entities/ApiModel/SignatureDefault";
import { SignatureDefaultGroup } from "entities/UIModel/SignatureDefaultGroup";
import { SignatureImageAgencyLocation } from "entities/UIModel/SignatureImageAgencyLocation";
import { StoreActionApi } from "react-sweet-state";

import { axiosSecuredInstance } from "configurations/axiosConfig";
import { createHook } from "react-sweet-state";
import { createStore } from "react-sweet-state";
// import { groupBy } from "lodash";
import { SignatureSearchCriteria } from "entities/UIModel";

export interface DefaultFormData {
  existingUserAgencySignatureDefaultIDs: string;
  signatureImage: SelectFieldOption;
  level: SelectFieldOption;
  location: SelectFieldOption;
  agency: SelectFieldOption;
  state: SelectFieldOption;
  loadingProgress?: number;
  isLoading?: boolean;
}

type State = {
  error: any;
  isLoading: boolean;
  agencyLocationsByImage: SignatureImageAgencyLocation[];
  signatures: Array<SelectFieldOption>;
  defaults: SignatureDefaultGroup[];
  initialDefault: DefaultFormData;
  agencyWideLevelCode: string;
  userOnlyLevelCode: string;
  defaultStates: Array<SelectFieldOption>;
  loadingProgress: number;
};

type Actions = typeof actions;

type StoreApi = StoreActionApi<State>;

const setError =
  (error: string | any) =>
    ({ setState }: StoreApi) => {
      setState({ error });
    };

const getData =
  (
    url: string,
    action: (param: Array<any>) => ({ setState }: StoreApi) => any,
    config?: AxiosRequestConfig
  ) =>
    async ({ dispatch }: StoreApi) => {
      try {
        const { data } = await axiosSecuredInstance.get(url, config);
        dispatch(action(data));
        return data;
      } catch (error) {
        dispatch(setError(error));
      }
    };

const setDefaults =
  (
    data: Array<SignatureDefault>, 
    updateState: boolean = false
  ) =>
    ({ setState }: StoreApi) => {
      // const grouped = Object.values(
      //   groupBy(data, d => `${d.SignatureImageName} | ${d.AgencyID} | ${d.StateCode} | ${d.LocationDisplayName} | ${d.SignatureLevelTypeCode}`)
      // )
      //   .map(g => {
      //     const firstItem = g[0];
      //     const group: SignatureDefaultGroup = {
      //       key: `${firstItem.SignatureImageName} | ${firstItem.AgencyID} | ${firstItem.StateCode} | ${firstItem.LocationDisplayName} | ${firstItem.SignatureLevelTypeCode}`,
      //       existingUserAgencySignatureDefaultIDs: g.map(i => i.UserAgencySignatureDefaultID).sort().join("|"),
      //       agencyID: firstItem.AgencyID,
      //       agencyName: firstItem.AgencyName,
      //       stateCode: firstItem.StateCode,
      //       state: firstItem.State,
      //       locationUniqueIDs: g.map(i => i.LocationUniqueID).sort().join("|"),
      //       locationDisplayName: firstItem.LocationDisplayName,
      //       signatureLevelTypeCode: firstItem.SignatureLevelTypeCode,
      //       signatureLevelTypeName: firstItem.SignatureLevelTypeName,
      //       signatureImageID: firstItem.SignatureImageID,
      //       signatureImageName: firstItem.SignatureImageName,
      //       signatureCreatedByUser: firstItem.SignatureCreatedByUser,
      //       signatureCreatedDate: firstItem.SignatureCreatedDate,
      //       integrationKey: firstItem.integrationKey,
      //       agencyLocation :firstItem.AgencyLocation,
      //       signatureImage :firstItem.SignatureImageData,
      //       isModify :firstItem.IsModify,
      //     };
      //     return group;
      //   });
      const  mappedDefaultGroups: SignatureDefaultGroup[] = 
        data?.map((sd) => {
          const group: SignatureDefaultGroup = {
            key: sd.DefaultKeys,
            existingUserAgencySignatureDefaultIDs: sd.DefaultIDs,
            agencyID: sd.AgencyID,
            agencyName: sd.AgencyName,
            stateCode: sd.StateCode,
            state: sd.State,
            locationUniqueIDs: sd.LocationUniqueIDs.split("|").sort().join("|"),
            locationDisplayName: sd.LocationDisplayName,
            signatureLevelTypeCode: sd.SignatureLevelTypeCode,
            signatureLevelTypeName: sd.SignatureLevelTypeName,
            signatureImageID: sd.SignatureImageID,
            signatureImageName: sd.SignatureImageName,
            signatureCreatedByUser: sd.SignatureCreatedByUser,
            signatureCreatedDate: sd.SignatureCreatedDate,
            integrationKey: sd.IntegrationKey,
            agencyLocation: sd.AgencyLocation,
            signatureImage: sd.SignatureImageData,
            isModify: sd.IsModify,
            rowsPerPage: sd.RowsPerPage,
            currentRow: sd.CurrentRow,
            totalRows: sd.TotalRows,
            currentPage: sd.CurrentPage,
            totalPages: sd.TotalPages,
          };
          return group;          
        })

        if (updateState) {
          setState({
            defaults: mappedDefaultGroups,
          });
        }

        return mappedDefaultGroups;
    };

const setDefaultStates =
  (data: Array<any>) =>
    ({ setState }: StoreApi) => {
      const mapped: SelectFieldOption[] = data.map((s: any) => ({
        text: s.StateAbbr,
        value: s.StateCode,
      }));

      setState({
        defaultStates: mapped,
      });
    };

const setAgencyLocationsByImage =
  (data: Array<any>) =>
    ({ setState }: StoreApi) => {
      const mapped: SignatureImageAgencyLocation[] = data.map(l => ({
        agencyID: l.AgencyID,
        agencyName: l.AgencyName,
        locationDisplayName: l.LocationDisplayName,
        locationID: l.LocationID,
        locationUniqueID: l.LocationUniqueID,
        stateAbbr: l.StateAbbr,
        stateCode: l.StateCode,
      }));
      setState({
        agencyLocationsByImage: mapped
      });
    };

const setSignatures =
  (data: Array<any>) =>
    ({ setState }: StoreApi) => {
      setState({
        signatures: data.map((signature) => ({
          text: signature.SignatureImageName,
          value: signature.SignatureImageID,
        })),
      });
    };

const emptyField = {
  text: "",
  value: "",
  integrationKey: "",
};

const setColumns =
  (columns: any[]) =>
  () => {
    const gridColumns = Array.isArray(columns) ? columns : [];

    localStorage.setItem(
      LocalStorageKeys.COLUMNS_SIGNATURE,
      JSON.stringify(gridColumns)
    );

    return { gridColumns };
  };
  
const initialState = {
  error: false,
  isLoading: false,
  loadingProgress: 0,
  agencyLocationsByImage: [],
  signatures: [],
  defaults: [],
  defaultStates: [],
  initialDefault: {
    signatureImage: emptyField,
    level: emptyField,
    location: emptyField,
    agency: emptyField,
    state: emptyField,
    existingUserAgencySignatureDefaultIDs: "",
  },
  agencyWideLevelCode: "",
  userOnlyLevelCode: "",
};

const actions = {
  getDefaults:
  (agencyId: string = "", criteria?: SignatureSearchCriteria, updateState: boolean = false) =>
  async ({ dispatch, setState, getState }: StoreApi): Promise<SignatureDefaultGroup[]> => {

    setState({ isLoading: true, loadingProgress: 0 });

    const progressInterval = setInterval(() => {
      const currentProgress = getState().loadingProgress;
      setState({
        loadingProgress: Math.min(currentProgress + 10, 90),
      });
    }, 100);

    try {
      const payload = {
        ...criteria,
        agencyId: agencyId?.trim() ? agencyId : null,
      };

      const { data } = await axiosSecuredInstance.get<SignatureDefault[]>(
        `/Signature/v2/Defaults/GetDefaults`,
        {
          params: payload,
        }
      );

      clearInterval(progressInterval);
      setState({ loadingProgress: 100, isLoading: false });
      return dispatch(setDefaults(data, updateState));
      // return data;
    } catch (error) {
      clearInterval(progressInterval);
      setState({ isLoading: false, loadingProgress: 0 });
      throw error;
    }
  },
  clearAgencyLocationsByImage:
    () =>
    ({ setState }: StoreApi) => {
      setState({
        agencyLocationsByImage: [],
      });
    },
  getDefaultState:
    (agencyId: string = "") =>
    async ({ dispatch }: StoreApi) => {
      const { data } = await axiosSecuredInstance.get<SignatureDefault[]>(
        `/Signature/v2/Defaults/GetStates/${agencyId}`,
      );
      dispatch(setDefaultStates(data));
    },
  getAgencyLocationsByImage:
    (signatureImageID: number, agencyId: string = "") =>
    async ({ dispatch }: StoreApi) => {
      dispatch(
        getData(
          `/Signature/v2/Defaults/GetAgencyLocationsByImage/${signatureImageID}
            ?AgencyId=${agencyId}`,
          setAgencyLocationsByImage,
        ),
      );
    },

  setAgencyWideLevelCode:
    (code: string) =>
    ({ setState }: StoreApi) => {
      setState({
        agencyWideLevelCode: code,
      });
    },
  setUserOnlyLevelCode:
    (code: string) =>
    ({ setState }: StoreApi) => {
      setState({
        userOnlyLevelCode: code,
      });
    },
  saveDefault:
    (signatureDefaultGroup: SignatureDefaultGroup) =>
    async ({ dispatch, setState }: StoreApi) => {
      try {
        const response = await axiosSecuredInstance.post("/Signature/v2/Defaults/Group", signatureDefaultGroup);

        return response.status;
      } catch (error: any) {
        dispatch(() => {
          setState({ error });
        });
        return error.response!.status;
      }
    },
    setColumns:
    (columns: any[]) =>
    ({ dispatch }: StoreApi) => {
      // Store only valid columns if you want to be safe
      const gridColumns = Array.isArray(columns) ? columns : [];

      localStorage.setItem(
        LocalStorageKeys.COLUMNS_SIGNATURE,
        JSON.stringify(gridColumns)
      );

      return { gridColumns };
    },
  deleteDefault:
    (signatureDefaultIds: string) =>
    async ({ dispatch, setState }: StoreApi) => {
      try {
        const ids = signatureDefaultIds.split("|").map((id) => Number(id));
        const response = await axiosSecuredInstance.delete("/Signature/v2/Defaults/Group", { data: ids });
        return response.status;
      } catch (error: any) {
        dispatch(() => {
          setState({ error });
        });
        return error.response!.status;
      }
    },
    getInitialColumnsDefintion:
    (hidden: boolean) =>
    () => {
      const key = LocalStorageKeys.COLUMNS_SIGNATURE;
      const existingColumns = localStorage.getItem(key) || "[]";
      return JSON.parse(existingColumns);
    },

  getColumnsDefinition:
    () =>
    async ({ dispatch }: StoreApi) => {
      const { data } = await axiosSecuredInstance.get(
        "/UISettings/grids/columnSettings/defaultsignatures"
      );
      return dispatch(setColumns(data));
    },
    setColumnDefinition:
    (
      fields: string[],
      propName: keyof Omit<IColumn, "field" | "name">,
      value?: string | number | boolean | undefined
    ) =>
    async ({ dispatch }: StoreApi) => {
      let data: IColumn[] = [];
  
      if (propName === "position") {
        data = fields.map((field, index) => ({
          field,
          name: "",
          position: index,
        }));
      } else {
        data = fields.map((field) => ({
          field,
          name: "",
          [propName]: value,
        }));
      }
  
      try {
        const response = await axiosSecuredInstance.post("/UISettings/grids/columnSettings/defaultsignatures", data);
        if (response.data) {
          dispatch(setColumns(response.data));
        }
      } catch (error) {
        console.error(error);
      }
    },
  

  getSignatures:
    (agencyID: string, StateCode: string, LocationID: string) =>
    async ({ dispatch }: StoreApi) => {
      dispatch(getData(`/Signature/v2/Defaults/GetSignatures/${agencyID}/${StateCode}/${LocationID}`, setSignatures));
    },
  clearSignatures:
    () =>
    ({ setState }: StoreApi) => {
      setState({
        signatures: [],
      });
    },
  editSetup:
    (signatureDefaultGroup: SignatureDefaultGroup) =>
    ({ setState }: StoreApi) => {
      let initialValue: DefaultFormData = {
        existingUserAgencySignatureDefaultIDs: signatureDefaultGroup.existingUserAgencySignatureDefaultIDs,
        agency: {
          value: signatureDefaultGroup.agencyID,
          text: signatureDefaultGroup.agencyName,
        },
        state: {
          value: signatureDefaultGroup.stateCode,
          text: signatureDefaultGroup.state,
        },
        location: {
          value: signatureDefaultGroup.locationUniqueIDs,
          text: signatureDefaultGroup.locationDisplayName,
        },
        level: {
          value: signatureDefaultGroup.signatureLevelTypeCode,
          text: signatureDefaultGroup.signatureLevelTypeName,
        },
        signatureImage: {
          value: signatureDefaultGroup.signatureImageID.toString(),
          text: signatureDefaultGroup.signatureImageName,
        },
      };
      setState({
        initialDefault: initialValue,
      });
    },
  resetState:
    () =>
    ({ setState, getState }: StoreApi) => {
      const { defaults, agencyWideLevelCode, userOnlyLevelCode } = getState();
      setState({
        ...initialState,
        defaults,
        agencyWideLevelCode,
        userOnlyLevelCode,
      });
    },
};

const Store = createStore<State, Actions>({
  initialState,
  actions,
  name: "signatureDefaultsContext",
}); 

const getagencyLocationsByImage = (state: State) => ({
  agencyLocationsByImage: state.agencyLocationsByImage,
})

const useAgencyLocationByImage  = createHook(Store, {
  selector: getagencyLocationsByImage,
})

const getDefaultsState = (state: State) => ({
  defaults: state.defaults,
})

const useDefaultsState  = createHook(Store, {
  selector: getDefaultsState,
})

const hook = createHook(Store);
export const useSignatureDefaults = () => { return hook(); };

export {
  useAgencyLocationByImage,
  useDefaultsState,
}